import {
  useForm,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormReset,
  type UseFormSetValue,
} from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { COUNTRY_TW } from "@/constants/common";
import { PaymentMethodId, ShippingMethodId } from "@/constants/enums";
import type { ShippingAddress } from "@/types/common";

const validateConditionalFields = (data: FormSchemaType, ctx: z.RefinementCtx) => {
  // 根據運送方式驗證必要的可選欄位
  const isCVSshipping = data.shippingMethodId.includes("Wooecpay_Logistic_CVS");
  const isPostShipping = data.shippingMethodId === ShippingMethodId.FLAT_RATE;

  if (isCVSshipping) {
    if (!data.cvsStoreId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom, // 自訂錯誤類型
        path: ["cvsStoreId"], // 錯誤欄位名稱
        message: "請選擇超商門市", // 錯誤訊息
      });
    }
  }

  if (isPostShipping) {
    if (!data.shippingAddress.postcode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["shippingAddress.postcode"],
        message: "請填寫郵遞區號",
      });
    } else if (!/^\d{3,5}$/.test(data.shippingAddress.postcode)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["shippingAddress.postcode"],
        message: "郵遞區號格式不正確，請填寫 3 至 5 位數字",
      });
    }
    if (!data.shippingAddress.state) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["shippingAddress.state"],
        message: "請填寫縣/市",
      });
    }
    if (!data.shippingAddress.city) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["shippingAddress.city"],
        message: "請填寫區/鄉/鎮/市",
      });
    }
    if (!data.shippingAddress.address_1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["shippingAddress.address_1"],
        message: "請填寫街道/樓層",
      });
    }
  }
};

// 所有使用者填寫的欄位（包括根據運送方式條件性需要或不需要的欄位）
const formSchema = z
  .object({
    email: z.string().email("Email 格式錯誤"),
    shippingAddress: z.object({
      first_name: z.string().nonempty("請填寫名字"),
      last_name: z.string().nonempty("請填寫姓氏"),
      company: z.string().optional(),
      address_1: z.string().optional(),
      address_2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postcode: z.string().optional(),
      country: z.literal(COUNTRY_TW),
      phone: z.string().regex(/^09\d{8}$/, "手機號碼格式不正確，請填寫例如 0912xxx999"),
    }),
    shippingMethodId: z.nativeEnum(ShippingMethodId, {
      errorMap: () => ({ message: "請選擇運送方式" }),
    }),
    paymentMethodId: z.nativeEnum(PaymentMethodId, {
      errorMap: () => ({ message: "請選擇付款方式" }),
    }),
    customNote: z.string().optional(),
    cvsStoreId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    validateConditionalFields(data, ctx);
  });

//#region Types
export type FormSchemaType = z.infer<typeof formSchema>;

// 所有使用者填寫的欄位（包括根據運送方式條件性需要或不需要的欄位）
export interface CheckoutForm {
  email: string;
  shippingAddress: ShippingAddress;
  shippingMethodId: ShippingMethodId | undefined;
  paymentMethodId: PaymentMethodId | undefined;
  customNote?: string;
  cvsStoreId?: string;
}

export interface UseCheckoutFormReturn {
  register: UseFormRegister<FormSchemaType>;
  control: Control<FormSchemaType>;
  errors: FieldErrors<CheckoutForm>;
  setValue: UseFormSetValue<CheckoutForm>;
  getValues: UseFormGetValues<CheckoutForm>;
  reset: UseFormReset<CheckoutForm>;
  setError: (name: keyof FormSchemaType, error: { type: string; message?: string }) => void;
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  shippingMethodIdField: ShippingMethodId | undefined;
}
//#endregion

const defaultFormValues: CheckoutForm = {
  email: "",
  shippingAddress: {
    first_name: "",
    last_name: "",
    company: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    postcode: "",
    country: COUNTRY_TW,
    phone: "",
  },
  paymentMethodId: undefined,
  shippingMethodId: undefined,
  customNote: "",
  cvsStoreId: "",
};

const useCheckoutForm = () => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
    reset,
    setError,
    handleSubmit,
  } = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  const shippingMethodIdField = useWatch({
    control: control,
    name: "shippingMethodId",
  });

  return {
    register,
    control,
    errors,
    setValue,
    getValues,
    watch,
    reset,
    setError,
    handleSubmit,
    shippingMethodIdField,
  };
};

export default useCheckoutForm;
